import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X, Loader2, Heart, Shield, Users, Award, Sparkles, Zap, Target, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Available icons for core values
const availableIcons = [
  { name: 'Heart', icon: Heart },
  { name: 'Shield', icon: Shield },
  { name: 'Users', icon: Users },
  { name: 'Award', icon: Award },
  { name: 'Sparkles', icon: Sparkles },
  { name: 'Zap', icon: Zap },
  { name: 'Target', icon: Target },
  { name: 'Eye', icon: Eye }
];

const iconMap = {
  Heart, Shield, Users, Award, Sparkles, Zap, Target, Eye
};

export const CoreValuesEditor = () => {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingValue, setEditingValue] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    icon: 'Heart',
    title: '',
    description: '',
    order: 0
  });

  useEffect(() => {
    fetchValues();
  }, []);

  const fetchValues = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/core-values`);
      setValues(response.data);
    } catch (error) {
      console.error('Error fetching core values:', error);
      toast.error('Failed to fetch core values');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconChange = (value) => {
    setFormData(prev => ({
      ...prev,
      icon: value
    }));
  };

  const resetForm = () => {
    setFormData({
      icon: 'Heart',
      title: '',
      description: '',
      order: values.length + 1
    });
    setEditingValue(null);
    setIsAdding(false);
  };

  const handleEdit = (value) => {
    setEditingValue(value);
    setFormData({
      icon: value.icon,
      title: value.title,
      description: value.description,
      order: value.order
    });
    setIsAdding(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this core value?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/core-values/${id}`);
      toast.success('Core value deleted successfully');
      fetchValues();
    } catch (error) {
      console.error('Error deleting core value:', error);
      toast.error('Failed to delete core value');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingValue) {
        await axios.put(`${BACKEND_URL}/api/core-values/${editingValue.id}`, formData);
        toast.success('Core value updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/core-values`, {
          ...formData,
          order: values.length + 1
        });
        toast.success('Core value created successfully');
      }

      resetForm();
      fetchValues();
    } catch (error) {
      console.error('Error saving core value:', error);
      toast.error('Failed to save core value');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="animate-spin text-brand-purple" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-gray-900">Core Values</h2>
          <p className="text-gray-600 font-body mt-1">Manage the core values displayed on the About page</p>
        </div>
        {!isAdding && (
          <Button
            onClick={() => {
              setFormData({
                icon: 'Heart',
                title: '',
                description: '',
                order: values.length + 1
              });
              setIsAdding(true);
            }}
            className="bg-brand-purple text-white hover:bg-brand-purple/90 font-body"
            data-testid="add-core-value-button"
          >
            <Plus size={18} className="mr-2" />
            Add New Value
          </Button>
        )}
      </div>

      {isAdding && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-heading text-lg font-semibold">
              {editingValue ? 'Edit Core Value' : 'Add New Core Value'}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetForm}
              className="text-gray-500"
            >
              <X size={18} />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="icon">Icon *</Label>
                <Select value={formData.icon} onValueChange={handleIconChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select an icon" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableIcons.map((iconItem) => {
                      const IconComp = iconItem.icon;
                      return (
                        <SelectItem key={iconItem.name} value={iconItem.name}>
                          <div className="flex items-center gap-2">
                            <IconComp size={18} />
                            <span>{iconItem.name}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Comfort First"
                  className="mt-1 font-body"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                name="description"
                required
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe this core value..."
                className="mt-1 font-body"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="font-body"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-brand-purple text-white hover:bg-brand-purple/90 font-body"
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {editingValue ? 'Update' : 'Create'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {values.map((value, index) => {
          const IconComponent = iconMap[value.icon] || Heart;
          return (
            <motion.div
              key={value.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl border border-gray-200 p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-brand-purple/10 rounded-xl flex items-center justify-center">
                  <IconComponent className="text-brand-purple" size={24} />
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(value)}
                    className="text-gray-500 hover:text-brand-purple"
                    data-testid={`edit-value-${value.id}`}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(value.id)}
                    className="text-gray-500 hover:text-red-600"
                    data-testid={`delete-value-${value.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
              
              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                {value.title}
              </h3>
              <p className="text-gray-600 font-body text-sm">
                {value.description}
              </p>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-400 font-body">
                  Order: {value.order} | Icon: {value.icon}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {values.length === 0 && !isAdding && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <p className="text-gray-500 font-body">No core values yet. Add your first one!</p>
        </div>
      )}
    </div>
  );
};
